import React, { useState, useEffect } from "react";
import { getRightworkAPI } from "../requests/utilities/requests";
import { postRightworkAPI } from "../requests/utilities/requests";
import {
  API_get_pay_periods,
  API_get_timecards_for_employee,
  API_manage_timecard_change_request,
  API_post_pay_period_approval,
  API_post_timecard_change_request,
  API_get_roles
} from "../../../constants";
import TimeEntriesHeaderEmployee from "./TimeEntriesHeaderEmployee";
import {
  formatDate,
  formatDateReadableWithDayOfWeekNoYear,
  formatDateReadable,
} from "../requests/utilities/helpers";
import LoadSpinner from "../../../utilities/LoadSpinner";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import "react-toastify/dist/ReactToastify.css";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EarningsBarChart from "./EarningsBarChart";
import { hoursToMinutes } from "../../../helpers/helpers";
import TimeEntriesReviewCard from "./TimeEntriesReviewCard";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import TimecardHistoryTooltip from "./TimecardHistoryTooltip";
import TimecardChangeRequestModal from "./TimecardChangeRequestModal";
import TimecardChangeRequestsPanel from "./TimecardChangeRequestsPanel";

function TimeEntriesDisplay(props) {
  const default_data = {
    clocked_in_at: null,
    clocked_out_at: null,
    clocked_in_at_date_time: new Date(),
    clocked_out_at_date_time: new Date(),
    declared_cash_tips: null,
    published_schedule: null,
    status: null,
    store_team_member: {
      first_name: "",
      last_name: "",
      id: null,
    },
    wage_type: null,
    timecard_breaks: [],
    uuid: null,
  };

  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [modalData, setModalData] = React.useState(default_data);
  const [modalEdit, setModalEdit] = React.useState(false);

  const [showSettings, setShowSettings] = React.useState(false);

  const [payPeriods, setPayPeriods] = useState([]);

  const [timeEntries, setTimeEntries] = useState([]);
  const [paidBreakTrackingEnabled, setPaidBreakTrackingEnabled] = useState(false);
  const [summaryList, setSummaryList] = React.useState([]);
  const [timecardChangeRequests, setTimecardChangeRequests] = useState([]);
  const [payPeriodStatus, setPayPeriodStatus] = useState(null);
  const [requestModalEntry, setRequestModalEntry] = useState(null);
  const [editingChangeRequest, setEditingChangeRequest] = useState(null);
  const [requestModalOpen, setRequestModalOpen] = useState(false);

  const [selectedPayPeriod, setSelectedPayPeriod] = React.useState(null)
  const [selectedPayPeriodDates, setSelectedPayPeriodDates] = useState([]);

  const [showTimeEntries, setShowTimeEntries] = useState(false);

  const [isLoading, setIsLoading] = React.useState(false);
  const [rolesList, setRolesList] = React.useState([]);

  // console.log("PAY PERIODS", payPeriods)
  // console.log("TIME ENTRIES", timeEntries)

  useEffect(() => {
    setShowTimeEntries(false);
    fetchPayPeriods(true);
    fetchRoles();
  }, [props.homeStore]);

  const fetchPayPeriods = async (showLoading) => {
    setIsLoading(showLoading);
    let api_params = { store_id: props.homeStore };
    let api = API_get_pay_periods;
    try {
      const res = await getRightworkAPI(api, api_params);
      // console.log(res);
      if (res.status === 200) {
        const data = res.data?.redux?.payload?.pay_periods;
        setPayPeriods(data);
        setIsLoading(false);
      }
    } catch (error) {
      throw Error(error);
    }
  };

  // console.log("PAY PERIOD WHEN EMPLOYEE", payPeriods)

  const fetchTimeCardsForEmployee = async (showLoading, date) => {
    setIsLoading(showLoading);
    let api_params = {
      store_id: props.homeStore,
      date: date,
    };
    let api = API_get_timecards_for_employee;
    try {
      const res = await getRightworkAPI(api, api_params);
      if (res.status === 200) {
        // console.log("TIMECARDS", res.data.redux?.payload?.timecards)
        setTimeEntries(res.data?.redux?.payload?.timecards);
        setSummaryList(res.data?.redux?.payload?.summary_list);
        setPaidBreakTrackingEnabled(res.data?.redux?.payload?.paid_break_tracking_enabled ?? false);
        setTimecardChangeRequests(res.data?.redux?.payload?.timecard_change_requests || []);
        setPayPeriodStatus(res.data?.redux?.payload?.pay_period_status || null);
        setIsLoading(false);
      }
    } catch (error) {
      throw Error(error);
    }
  };

  const submitTimecardChangeRequest = async (requestPayload) => {
    const isUpdate = Boolean(requestPayload.timecard_change_request_uuid);
    await postRightworkAPI(
      isUpdate ? API_manage_timecard_change_request : API_post_timecard_change_request,
      {
      store_id: props.homeStore,
      pay_period_uuid: selectedPayPeriod?.uuid,
      ...requestPayload,
      ...(isUpdate ? { action: "update" } : {}),
      timecard_breaks: JSON.stringify(requestPayload.timecard_breaks || []),
      }
    );
    toast.success(isUpdate ? "Time entry request updated" : "Time entry request submitted");
    await fetchTimeCardsForEmployee(false, selectedPayPeriodDates[0]);
  };

  const openTimecardRequest = (entry = null, changeRequest = null) => {
    setRequestModalEntry(entry);
    setEditingChangeRequest(changeRequest);
    setRequestModalOpen(true);
  };

  const closeTimecardRequest = () => {
    setRequestModalOpen(false);
    setRequestModalEntry(null);
    setEditingChangeRequest(null);
  };

  const deleteTimecardChangeRequest = async (changeRequest) => {
    if (!window.confirm("Delete this pending time entry request?")) return;
    try {
      await postRightworkAPI(API_manage_timecard_change_request, {
        store_id: props.homeStore,
        timecard_change_request_uuid: changeRequest.uuid,
        action: "delete",
      });
      toast.success("Time entry request deleted");
      await fetchTimeCardsForEmployee(false, selectedPayPeriodDates[0]);
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message || "Time entry request could not be deleted"
      );
    }
  };

  const pendingRequestForTimecard = (timecardUuid) =>
    timecardChangeRequests.find(
      (request) => request.timecard_uuid === timecardUuid && request.status === "pending"
    );

  const payPeriodIsMutable =
    props.timeEntriesEnabled && ["in_progress", "pending"].includes(payPeriodStatus);

  const fetchRoles = async () => {
    let api_params = {
      store_id: props.homeStore,
    };
    let api = API_get_roles;
    try {
      const res = await getRightworkAPI(api, api_params);
      if (res.status === 200) {
        setRolesList(res.data);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  const handleSelectPayPeriod = (pay_period, beginning_date, end_date) => {
    setShowTimeEntries(true);
    setSelectedPayPeriod(pay_period)
    setSelectedPayPeriodDates([beginning_date, end_date]);
    fetchTimeCardsForEmployee(true, beginning_date);
  };

  const handleGoBack = () => {
    // Deselect the current period and go back to the pay period list
    setShowTimeEntries(false);
  };

  // Enhancing the status lookup to include both label and styling
  // Simplified for employee view: auto_flag shows as Pending
  const statusStyles = {
    auto_approved: {
      label: "Approved",
      classes: "bg-emerald-50 border border-emerald-300 text-emerald-800",
    },
    auto_flag: {
      label: "Pending",
      classes: "bg-yellow-50 border border-yellow-300 text-yellow-800",
    },
    approved: {
      label: "Approved",
      classes: "bg-emerald-50 border border-emerald-300 text-emerald-800",
    },
    denied: {
      label: "Denied",
      classes: "bg-rose-50 border border-rose-300 text-red-800",
    },
    pending: {
      label: "Pending",
      classes: "bg-yellow-50 border border-yellow-300 text-yellow-800",
    },
  };

  const employeeApprovalStatusStyles = {
    approved: {
      label: "approved",
      classes: "border border-emerald-300 bg-emerald-50 text-emerald-800",
    },
    disputed: {
      label: "disputed",
      classes: "border border-rose-300 bg-rose-50 text-rose-800",
    },
    missed_approval: {
      label: "missed approval",
      classes: "border border-orange-300 bg-orange-50 text-orange-800",
    },
    pending_approval_initial: {
      label: "pending review",
      classes: "border border-amber-300 bg-amber-50 text-amber-800",
    },
    pending_approval_again: {
      label: "pending 2nd review",
      classes: "border border-amber-300 bg-amber-50 text-amber-800",
    },
  };

  // Existing states and hooks...
  const [nameFilter, setNameFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Assuming you've fetched timeEntries, now let's prepare dynamic filter options
  useEffect(() => {
    // This effect could be used to further manipulate timeEntries data if needed
  }, [timeEntries]);

  // Unique names for the Name filter dropdown
  const uniqueNames = [
    ...new Set(
      timeEntries.map(
        (entry) =>
          `${entry.store_team_member.first_name} ${entry.store_team_member.last_name}`
      )
    ),
  ];

  const uniqueStoreTeamMembers = [];
  const uniqueIds = new Set();

  timeEntries.forEach((entry) => {
    const memberId = entry.store_team_member.id;
    if (!uniqueIds.has(memberId)) {
      uniqueIds.add(memberId);
      uniqueStoreTeamMembers.push(entry.store_team_member);
    }
  });


  const filteredTimeEntries = timeEntries.filter((entry) => {
    const fullName = `${entry.store_team_member.first_name} ${entry.store_team_member.last_name}`;
    const entryDate = formatDate(
      new Date(entry.clocked_in_at).toLocaleDateString()
    );
    // Apply name filter
    const matchesName = nameFilter ? fullName === nameFilter : true;
    // Apply date filter
    const matchesDate = dateFilter ? entryDate === dateFilter : true;
    // Apply status filter
    const matchesStatus = statusFilter ? entry.status === statusFilter : true;

    return matchesName && matchesDate && matchesStatus;
  });

  // console.log("MODAL DATA", modalData);

  const filteredTimeEntriesForApproveAll = filteredTimeEntries.filter(
    (entry) => {
      return entry.status === "pending" || entry.status === "auto_flag";
    }
  );

  let filteredTimeEntriesUUIDs = [];

  filteredTimeEntriesForApproveAll.forEach((timeEntry) =>
    filteredTimeEntriesUUIDs.push(timeEntry.uuid)
  );


  // Function to convert data to CSV format
  const convertToCSV = (data) => {
    const csvRows = [];
    // Headers
    const headers = [
      "Name",
      "Date",
      "Timesheet",
      "Hours Paid",
      "Unpaid Breaks",
      ...(paidBreakTrackingEnabled ? ["Paid Breaks"] : []),
      "Status",
    ];
    csvRows.push(headers.join(","));

    // Values
    for (const entry of data) {
      // Use pre-calculated values from backend API
      const paidHours = entry.paid_hours ?? 0;
      const breakMinutes = props.timeEntriesEnabled
        ? (entry.break_minutes ?? 0)
        : hoursToMinutes(Number(entry.unpaid_break_time));
      const paidBreakMinutes = entry.paid_break_minutes ?? 0;
      entry["clocked_in_at_date_time"] = new Date(entry.clocked_in_at);
      entry["clocked_out_at_date_time"] = new Date(
        entry.clocked_out_at ? entry.clocked_out_at : new Date().getTime()
      );
      entry["status"] = entry.clocked_out_at
        ? entry.status
        : "currently_working";
      entry["sub_status"] =
        !entry.clocked_out_at &&
        (entry.timecard_breaks?.[entry.timecard_breaks?.length - 1]
          ?.clocked_out_at ||
          entry.timecard_breaks?.length === 0)
          ? "Working"
          : !entry.clocked_out_at &&
            !entry.timecard_breaks?.[entry.timecard_breaks?.length - 1]
              ?.clocked_out_at
          ? (entry.timecard_breaks?.[entry.timecard_breaks?.length - 1]?.is_paid ? "On Paid Break" : "On Break")
          : null;

      const values = [
        `${entry.store_team_member.first_name} ${entry.store_team_member.last_name}`,
        `${formatDate(new Date(entry.clocked_in_at).toLocaleDateString())}`,
        `${new Date(entry.clocked_in_at).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })} - ${new Date(entry.clocked_out_at).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })}`,
        `${paidHours}hrs`,
        `${breakMinutes}min`,
        ...(paidBreakTrackingEnabled ? [`${paidBreakMinutes}min`] : []),
        `${entry.status}`,
      ];
      csvRows.push(values.join(","));
    }

    return csvRows.join("\n");
  };

  // Function to download CSV file
  const downloadCSV = () => {
    const csvData = convertToCSV(timeEntries);
    const blob = new Blob([csvData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    const datePayPeriod = `${selectedPayPeriodDates[0]}_to_${selectedPayPeriodDates[1]}`;
    const filename = `time_entries_${datePayPeriod}.csv`; // Add datePayPeriod to filename
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // console.log("PAY PERIODS", payPeriods)

  const aggregatedSummary = summaryList?.reduce(
    (acc, current) => {
      return {
        hours_scheduled: acc.hours_scheduled + Number(current.hours_scheduled),
        hours_worked: acc.hours_worked + Number(current.hours_worked),
        wages_scheduled: acc.wages_scheduled + Number(current.wages_scheduled),
        wages_earned: acc.wages_earned + Number(current.wages_earned),
      };
    },
    {
      hours_scheduled: 0,
      hours_worked: 0,
      wages_scheduled: 0,
      wages_earned: 0,
    }
  );

  // [POST] api/clock_info/post_pay_period_approval (company employee)
  // * store_id (auth)
  // * pay_period_uuid
  // * pay_period_approval_uuid [optional]
  // * status [optional]
  // * note [optional]
  const post_pay_period_approval = async (
    pay_period_uuid,
    pay_period_approval_uuid,
    status,
    note
  ) => {
    // Prepare data to send
    const requestData = {
      store_id: props.homeStore,
      pay_period_uuid: pay_period_uuid,
      pay_period_approval_uuid: pay_period_approval_uuid,
      status: status,
      note: note
    };

    try {
      toast.loading("Submitting...", { autoClose: false });

      const res = await postRightworkAPI(API_post_pay_period_approval, requestData);

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Submitted Successfully", { autoClose: 3000 });

        fetchPayPeriods(false);

        if (selectedPayPeriod && selectedPayPeriod.uuid === pay_period_uuid) {
          setSelectedPayPeriod(prev => ({
            ...prev,
            pay_period_approvals: [{
              ...prev.pay_period_approvals[0],
              status: status,
              note: note
            }]
          }));
        }
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
        // console.log("Error Info", error.response?.data?.system?.data);
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  return (
    <>
      {!isLoading ? (
        <div>
          {showTimeEntries ? (
            <div>
              <div>
                <div>
                  <TimeEntriesHeaderEmployee
                    title="Back to Pay Periods"
                    storeOptions={props.storeOptions}
                    homeStore={props.homeStore}
                    storeNames={props.storeNames}
                    change_home_store={props.change_home_store}
                    setCurrentTab={props.setCurrentTab}
                    handleGoBack={handleGoBack}
                    showTimeEntries={showTimeEntries}
                    setShowTimeEntries={setShowTimeEntries}
                    setShowSettings={setShowSettings}
                    timeEntriesEnabled={props.timeEntriesEnabled}
                    payrollEnabled={props.payrollEnabled}
                  />
                </div>
                <div className="mx-auto w-full max-w-6xl pb-8">
                <div className="px-6 mt-8 relative">
                  <div className="flex justify-between">
                    <div>
                      <h1 className="text-xl font-bold text-slate-900">Time Entries</h1>
                      <h3 className="mt-2 text-slate-500">
                        {`${formatDateReadable(
                          selectedPayPeriodDates[0]
                        )} to ${formatDateReadable(selectedPayPeriodDates[1])}`}
                      </h3>
                      {payPeriodIsMutable && (
                        <button
                          onClick={() => openTimecardRequest(null)}
                          className="mt-4 h-fit rounded-full bg-violet-700 px-5 py-2 text-sm font-semibold text-white hover:bg-violet-800"
                        >
                          Request missing time entry
                        </button>
                      )}
                    </div>
                    {/* <div className="flex items-center space-x-3">
                      {!timeEntries.some((entry) =>
                        [
                          "pending",
                          "currently_working",
                          "auto_flag",
                        ].includes(entry.status)
                      ) ? (
                        <Tooltip title="Download all time entries to a .csv file">
                          <div
                            className="px-5 py-2 rounded-full bg-orange-600 hover:bg-orange-700 flex items-center space-x-1 text-white cursor-pointer"
                            onClick={downloadCSV}
                          >
                            <div>
                              <DownloadIcon />
                            </div>
                            <p>Download</p>
                          </div>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Can only download when all time entries are approved or denied">
                          <div className="px-5 py-2 rounded-full bg-slate-200 flex items-center space-x-1 text-slate-400">
                            <div>
                              <DownloadIcon />
                            </div>
                            <p>Download</p>
                          </div>
                        </Tooltip>
                      )}
                    </div> */}
                  </div>
                  {props.payrollEmployeeReviewEnabled &&
                    <div className="absolute top-0 right-6">
                      <TimeEntriesReviewCard
                        selectedPayPeriod={selectedPayPeriod}
                        post_pay_period_approval={post_pay_period_approval}
                        numOfTimeEntries={timeEntries.length}
                        timeEntriesHoursRoundedToTenths={props.timeEntriesHoursRoundedToTenths}
                      />
                    </div>
                  }
                </div>
                {timecardChangeRequests.length > 0 && (
                  <div className="mt-6 px-6">
                    <TimecardChangeRequestsPanel
                      requests={timecardChangeRequests}
                      rolesList={rolesList}
                      employeeView
                      onEdit={(changeRequest) =>
                        openTimecardRequest(null, changeRequest)
                      }
                      onDelete={deleteTimecardChangeRequest}
                      readOnly
                    />
                  </div>
                )}
                <div className="mt-8 px-6 flex items-center space-x-6">
                  <div className="px-4 py-3 border border-slate-100 rounded-lg w-[200px]">
                    <p className="text-xs text-slate-900 font-semibold">
                      Scheduled
                    </p>
                    <div className="mt-2 flex space-x-10">
                      <div>
                        <p className="text-xxs text-slate-500">Hours</p>
                        <p className="text-lg text-slate-900">
                          {aggregatedSummary?.hours_scheduled?.toFixed(props.timeEntriesHoursRoundedToTenths ? 1 : 2)}
                        </p>
                      </div>
                      {!props.timeEntriesEnabled &&
                        props.payrollWagesEnabled && (
                          <div>
                            <p className="text-xxs text-slate-500">Earnings</p>
                            <p className="text-lg text-slate-900">
                              $
                              {aggregatedSummary?.wages_scheduled?.toLocaleString(
                                "en-US",
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              )}
                            </p>
                          </div>
                        )}
                    </div>
                  </div>
                  <div className="px-4 py-3 border border-slate-100 rounded-lg w-[200px]">
                    <p className="text-xs text-slate-900 font-semibold">
                      Actual
                    </p>
                    <div className="mt-2 flex space-x-10">
                      <div>
                        <p className="text-xxs text-slate-500">Hours</p>
                        <p className="text-lg text-slate-900">
                          {aggregatedSummary?.hours_worked?.toFixed(props.timeEntriesHoursRoundedToTenths ? 1 : 2)}
                        </p>
                      </div>
                      {!props.timeEntriesEnabled &&
                        props.payrollWagesEnabled && (
                          <div>
                            <p className="text-xxs text-slate-500">Earnings</p>
                            <p className="text-lg text-slate-900">
                              $
                              {aggregatedSummary?.wages_earned?.toLocaleString(
                                "en-US",
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              )}
                            </p>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
                {timeEntries.length === 0 ? (
                  <div className="mx-6 mt-8 rounded-xl border border-slate-200 bg-white px-6 py-12 text-center shadow-sm">
                    <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-violet-50">
                      <InfoOutlinedIcon sx={{ fontSize: 22, color: "#6d28d9" }} />
                    </div>
                    <h2 className="mt-4 text-base font-semibold text-slate-900">
                      No time entries yet
                    </h2>
                    <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
                      There are no completed time entries in this pay period.
                    </p>
                    {payPeriodIsMutable && (
                      <button
                        onClick={() => openTimecardRequest(null)}
                        className="mt-5 rounded-lg bg-violet-700 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-violet-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
                      >
                        Request missing time entry
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="mt-8">
                      <EarningsBarChart
                        barChartData={summaryList}
                        timeEntriesEnabled={props.timeEntriesEnabled}
                        payrollWagesEnabled={props.payrollWagesEnabled}
                        timeEntriesHoursRoundedToTenths={props.timeEntriesHoursRoundedToTenths}
                      />
                    </div>
                    <div className="mt-8 px-6">
                      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                        <div className="inline-block min-w-full align-middle">
                          <div className="overflow-hidden">
                            <table className="min-w-full divide-y divide-slate-200">
                              <thead className="bg-slate-50">
                            <tr>
                              <th
                                scope="col"
                                className="flex-1 py-3 pl-5 pr-3 text-left text-xs font-bold uppercase text-slate-500"
                              >
                                Date
                              </th>
                              <th
                                scope="col"
                                className="flex-1 px-3 py-3 text-left text-xs font-bold uppercase text-slate-500"
                              >
                                Timesheet
                              </th>
                              <th
                                scope="col"
                                className="flex-1 px-3 py-3 text-left text-xs font-bold uppercase text-slate-500"
                              >
                                Hours Paid
                              </th>
                              <th
                                scope="col"
                                className="flex-1 px-3 py-3 text-left text-xs font-bold uppercase text-slate-500"
                              >
                                Unpaid Breaks
                              </th>
                              {paidBreakTrackingEnabled && (
                                <th
                                  scope="col"
                                  className="flex-1 px-3 py-3 text-left text-xs font-bold uppercase text-slate-500"
                                >
                                  Paid Breaks
                                </th>
                              )}
                              {props.timeEntriesEnabled && (
                                <th
                                  scope="col"
                                  className="flex-1 px-3 py-3 text-left text-xs font-bold uppercase text-slate-500"
                                >
                                  Status
                                </th>
                              )}
                              <th
                                scope="col"
                                className="py-3 pl-3 pr-5 text-right text-xs font-bold uppercase text-slate-500"
                              >
                                Request
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 bg-white">
                            {filteredTimeEntries.map((entry, i) => {
                              // Use pre-calculated values from backend API
                              const paidHours = entry.paid_hours ?? 0;
                              const breakMinutes = props.timeEntriesEnabled
                                ? (entry.break_minutes ?? 0)
                                : hoursToMinutes(
                                    Number(entry.unpaid_break_time)
                                  );
                              const paidBreakMinutes = entry.paid_break_minutes ?? 0;
                              entry["clocked_in_at_date_time"] = new Date(
                                entry.clocked_in_at
                              );
                              entry["clocked_out_at_date_time"] = new Date(
                                entry.clocked_out_at
                                  ? entry.clocked_out_at
                                  : new Date().getTime()
                              );
                              entry["status"] = entry.clocked_out_at
                                ? entry.status
                                : "currently_working";
                              entry["sub_status"] =
                                !entry.clocked_out_at &&
                                (entry.timecard_breaks?.[
                                  entry.timecard_breaks?.length - 1
                                ]?.clocked_out_at ||
                                  entry.timecard_breaks?.length === 0)
                                  ? "Working"
                                  : !entry.clocked_out_at &&
                                    !entry.timecard_breaks?.[
                                      entry.timecard_breaks?.length - 1
                                    ]?.clocked_out_at
                                  ? (entry.timecard_breaks?.[entry.timecard_breaks?.length - 1]?.is_paid ? "On Paid Break" : "On Break")
                                  : null;
                              return (
                                <tr key={i} className="transition-colors hover:bg-slate-50">
                                  <td className="whitespace-nowrap py-4 pl-5 pr-3 text-sm font-medium text-slate-900">
                                    <div>
                                      {formatDateReadableWithDayOfWeekNoYear(
                                        formatDate(
                                          new Date(
                                            entry.clocked_in_at
                                          ).toLocaleDateString()
                                        )
                                      )}
                                    </div>
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-700">
                                    {entry.clocked_out_at ? (
                                      <div className="flex items-center gap-2">
                                        <span>
                                          {new Date(
                                            entry.clocked_in_at
                                          ).toLocaleTimeString([], {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })}{" "}
                                          -{" "}
                                          {new Date(
                                            entry.clocked_out_at
                                          ).toLocaleTimeString([], {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })}
                                        </span>
                                        {entry.history && entry.history.length > 0 && (
                                          <TimecardHistoryTooltip
                                            history={entry.history}
                                            timecardId={entry.uuid}
                                            permissionRole="employee"
                                            rolesList={rolesList}
                                          />
                                        )}
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-2">
                                        <span>
                                          {new Date(
                                            entry.clocked_in_at
                                          ).toLocaleTimeString([], {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })}{" "}
                                          -
                                        </span>
                                        {entry.history && entry.history.length > 0 && (
                                          <TimecardHistoryTooltip
                                            history={entry.history}
                                            timecardId={entry.uuid}
                                            permissionRole="employee"
                                            rolesList={rolesList}
                                          />
                                        )}
                                      </div>
                                    )}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-700">
                                    <div>{`${paidHours?.toFixed(props.timeEntriesHoursRoundedToTenths ? 1 : 2)}hrs`}</div>
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-700">
                                    <div>{`${breakMinutes}min`}</div>
                                  </td>
                                  {paidBreakTrackingEnabled && (
                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-700">
                                      <div>{`${paidBreakMinutes}min`}</div>
                                    </td>
                                  )}
                                  {props.timeEntriesEnabled && (
                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-700">
                                      {entry.clocked_out_at && (
                                        <div
                                          className={`inline-block px-3 py-1 rounded-lg text-sm font-medium whitespace-nowrap ${
                                            statusStyles[entry.status]
                                              ?.classes
                                          }`}
                                        >
                                          {statusStyles[entry.status]
                                            ?.label || ""}
                                        </div>
                                      )}
                                    </td>
                                  )}
                                  <td className="whitespace-nowrap py-4 pl-3 pr-5 text-right text-sm">
                                    {pendingRequestForTimecard(entry.uuid) ? (
                                      <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
                                        Pending
                                      </span>
                                    ) : entry.is_rightwork_timecard && entry.clocked_out_at && payPeriodIsMutable ? (
                                      <button
                                        onClick={() => openTimecardRequest(entry)}
                                        className="font-semibold text-violet-700 hover:text-violet-900"
                                      >
                                        Request edit
                                      </button>
                                    ) : null}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        {filteredTimeEntries.length === 0 && (
                          <div className="flex w-full justify-center border-t border-slate-200 px-6 py-10">
                            <div className="flex flex-col items-center">
                              <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                                <InfoOutlinedIcon sx={{ fontSize: 20, color: "#94a3b8" }} />
                              </div>
                              <p className="mt-3 text-sm text-slate-400">
                                No time entries match this filter criteria
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  </div>
                  </>
                )}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div>
                <TimeEntriesHeaderEmployee
                  title="Pay Periods"
                  storeOptions={props.storeOptions}
                  homeStore={props.homeStore}
                  storeNames={props.storeNames}
                  change_home_store={props.change_home_store}
                  setCurrentTab={props.setCurrentTab}
                  setShowSettings={setShowSettings}
                  timeEntriesEnabled={props.timeEntriesEnabled}
                  payrollEnabled={props.payrollEnabled}
                />
              </div>
              <div className="mx-auto mt-12 w-full max-w-6xl px-6 pb-8">
                <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                  <div className="inline-block min-w-full align-middle">
                    <div className="overflow-hidden">
                      <table className="min-w-full divide-y divide-slate-200">
                        <thead className="bg-slate-50">
                          <tr>
                            <th
                              scope="col"
                              className="flex-1 py-3 pl-5 pr-3 text-left text-xs font-bold uppercase text-slate-500"
                            >
                              Pay Period
                            </th>
                            {/* <th
                              scope="col"
                              className="flex-1 px-3 py-3.5 text-left text-xs font-semibold text-slate-500"
                            >
                              # Time Entries
                            </th> */}
                            <th
                              scope="col"
                              className="flex-1 px-3 py-3 text-left text-xs font-bold uppercase text-slate-500"
                            >
                              Hours
                            </th>
                            {!props.timeEntriesEnabled &&
                              props.payrollWagesEnabled && (
                                <th
                                  scope="col"
                                  className="flex-1 px-3 py-3 text-left text-xs font-bold uppercase text-slate-500"
                                >
                                  Earnings
                                </th>
                              )}
                            {props.payrollEmployeeReviewEnabled &&
                              <th
                                scope="col"
                                className="flex-1 px-3 py-3 text-left text-xs font-bold uppercase text-slate-500"
                              >
                                Approval Status
                              </th>
                            }
                            <th
                              scope="col"
                              className="relative w-1/12 py-3 pl-3 pr-5"
                            >
                              {/* <span className="sr-only">Edit</span> */}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {payPeriods?.map((period, i) => (
                            <tr
                              key={i}
                              className="group cursor-pointer transition-colors hover:bg-slate-50"
                              onClick={() =>
                                handleSelectPayPeriod(
                                  period,
                                  period.beginning_date,
                                  period.end_date
                                )
                              }
                            >
                              <td className="whitespace-nowrap py-4 pl-5 pr-3 text-sm">
                                <div className="font-medium text-slate-900">
                                  {formatDateReadable(period.beginning_date)} -{" "}
                                  {formatDateReadable(period.end_date)}
                                </div>
                              </td>
                              {/* <td className="whitespace-nowrap py-5 pl-6 pr-3 text-sm">
                                <div>
                                  4
                                </div>
                              </td> */}
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-700">
                                {period?.timecard_summary_data?.payable_hours?.toFixed(props.timeEntriesHoursRoundedToTenths ? 1 : 2)}
                              </td>
                              {!props.timeEntriesEnabled &&
                                props.payrollWagesEnabled && (
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-700">
                                    $
                                    {period?.timecard_summary_data?.total_pay.toLocaleString(
                                      "en-US",
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }
                                    )}
                                  </td>
                                )}
                              {props.payrollEmployeeReviewEnabled &&
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-700">
                                  <div className={`w-fit rounded px-2 py-1 ${employeeApprovalStatusStyles?.[period?.pay_period_approvals?.[0]?.status]?.classes}`}>
                                    {employeeApprovalStatusStyles?.[period?.pay_period_approvals?.[0]?.status]?.label}
                                  </div>
                                </td>
                              }
                              <td className="relative flex flex-row items-center justify-end gap-5 whitespace-nowrap py-4 pl-3 pr-5 text-right text-sm font-medium">
                                <div className="text-slate-400 transition-colors group-hover:text-slate-600">
                                  <ChevronRightIcon />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <LoadSpinner />
        </div>
      )}
      <TimecardChangeRequestModal
        open={requestModalOpen}
        onClose={closeTimecardRequest}
        entry={requestModalEntry}
        changeRequest={editingChangeRequest}
        onSubmit={submitTimecardChangeRequest}
        rolesList={rolesList}
        paidBreakTrackingEnabled={paidBreakTrackingEnabled}
        defaultDate={selectedPayPeriodDates[0]}
        timeEntriesRoleSelectionEnabled={props.timeEntriesRoleSelectionEnabled}
      />
    </>
  );
}

export default TimeEntriesDisplay;
